import { useEffect, useState, useCallback, useRef, memo, useMemo } from "react";
import {
    useToast, Box, Text, Grid, GridItem, Heading, Button, Input,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter,
    ModalBody, ModalCloseButton, useDisclosure, Select, Switch,
    Flex, Tag, TagLabel, TagLeftIcon, Badge, Tooltip,
    HStack, VStack, Divider, IconButton, Card, CardBody, CardHeader,
    SimpleGrid, Image, Spinner, useColorModeValue,
    FormControl, FormLabel,
    Drawer, DrawerBody, DrawerHeader, DrawerOverlay, DrawerContent, DrawerCloseButton,
    InputGroup, InputRightAddon,
    Checkbox
} from "@chakra-ui/react";
import { keyframes } from '@emotion/react';
import { CircleMarker, MapContainer, Polyline, TileLayer, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {
    AddIcon,
    CheckCircleIcon,
    WarningIcon,
    SettingsIcon,
    DeleteIcon,
    RepeatIcon,
    SearchIcon,
    EditIcon,
    ChevronLeftIcon,
    ChevronRightIcon
} from "@chakra-ui/icons";
import { FaCrop, FaFilter, FaVideo, FaCheckCircle, FaTimesCircle, FaSearch, FaInfoCircle, FaRobot, FaBus, FaUser, FaCar, FaMobileAlt, FaHardHat, FaBan, FaMapMarkerAlt, FaUndo, FaExternalLinkAlt } from 'react-icons/fa';
import { filterAllowedAlgorithms, getAlgorithmDisplayLabel, DEFAULT_FACE_SERVICE_CONFIG } from "../utils/algorithms";

// Componente Modal de Configurações Globais (design minimalista)
const GlobalThresholdsModal = ({ isOpen, onClose, defaultThresholds, onSave }) => {
    const buildDraftFrom = (src) => {
        const d = {};
        for (const inf of INFRACTION_TYPES) {
            d[inf.id] = thresholdToPercent(src?.[inf.id]);
        }
        return d;
    };

    const [draftThresholds, setDraftThresholds] = useState(() => buildDraftFrom(defaultThresholds));

    useEffect(() => {
        if (isOpen) setDraftThresholds(buildDraftFrom(defaultThresholds));
    }, [isOpen, defaultThresholds]);

    const handleChange = (infractionId, value) => {
        setDraftThresholds(prev => ({ ...prev, [infractionId]: value }));
    };

    const handleReset = () => {
        setDraftThresholds(buildDraftFrom(buildDefaultThresholds(INFRACTION_TYPES, DEFAULT_THRESHOLD)));
    };

    const handleSave = () => {
        const normalized = {};
        for (const [key, val] of Object.entries(draftThresholds)) {
            normalized[key] = percentToThreshold(val);
        }
        onSave(normalized);
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <Flex align="center" justify="space-between" width="100%">
                        <Text fontSize="lg" fontWeight="semibold">Thresholds Globais</Text>
                        <HStack spacing={2}>
                            <Button size="sm" variant="ghost" marginRight="15px" onClick={handleReset}>Restaurar padrão</Button>
                        </HStack>
                    </Flex>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <Text fontSize="sm" color="gray.500" mb={3}>
                        Valores em porcentagem (0–100%). Alterações serão aplicadas às câmeras que usam o valor padrão.
                    </Text>

                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                        {INFRACTION_TYPES.map(infraction => (
                            <FormControl key={infraction.id}>
                                <FormLabel fontSize="sm" mb={1}>{infraction.label}</FormLabel>
                                <InputGroup size="sm" width="120px">
                                    <Input
                                        type="number"
                                        min={0}
                                        max={100}
                                        step={1}
                                        value={draftThresholds[infraction.id] ?? ''}
                                        onChange={(e) => handleChange(infraction.id, e.target.value)}
                                        textAlign="right"
                                    />
                                    <InputRightAddon>%</InputRightAddon>
                                </InputGroup>
                            </FormControl>
                        ))}
                    </SimpleGrid>
                </ModalBody>
                <ModalFooter>
                    <Button colorScheme="teal" mr={3} onClick={handleSave} size="sm">Aplicar</Button>
                    <Button variant="ghost" onClick={onClose} size="sm">Fechar</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

const VEHICLE_TYPES = [
    { id: 'CARRO', label: 'Carro' },
    { id: 'MOTO', label: 'Moto' },
    { id: 'ÔNIBUS', label: 'Ônibus' },
    { id: 'CAMINHÃO', label: 'Caminhão' },
    { id: 'BICICLETA', label: 'Bicicleta' }
];

// Tipos de infração disponíveis para "Reconhecimento de Infrações (IA)"
const INFRACTION_TYPES = [
    { id: 'no_helmet', label: 'Sem Capacete' },
    { id: 'no_seatbelt', label: 'Sem Cinto de Segurança' },
    { id: 'no_rearview', label: 'Sem Retrovisor' },
    { id: 'no_hand', label: 'Sem Mãos no Guidão' },
    { id: 'on_truck', label: 'Pessoa na Caçamba' },
    { id: 'cellphone_infraction', label: 'Uso de Celular' },
    { id: 'sandals_infraction', label: 'Uso de Chinelo' }
];

// Tipos de veículo capturados pela "Faixa de Ônibus"
const BUS_LANE_VEHICLE_TYPES = [
    { id: 'carro', label: 'Carro' },
    { id: 'moto', label: 'Moto' },
];

// Opções de direção para infração
const DIRECTION_OPTIONS = [
    { value: '', label: 'Não Aplicável' },
    { value: 'front', label: 'Frente (veículo de frente para câmera)' },
    { value: 'back', label: 'Traseira (veículo de costas para câmera)' }
];

const DEFAULT_THRESHOLD = 0.45;

const WRONG_WAY_THRESHOLD_TYPES = [
    { id: 'car_front', label: 'Carro frente' },
    { id: 'car_back', label: 'Carro traseira' },
    { id: 'has_rearview', label: 'Moto com retrovisor' },
    { id: 'no_rearview', label: 'Moto sem retrovisor' }
];

const buildDefaultThresholds = (items, value = DEFAULT_THRESHOLD) => Object.fromEntries(
    items.map(item => [item.id, value])
);

const normalizeThresholdValue = (value) => {
    if (value === undefined || value === null || value === '') return null;
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return null;
    const normalizedValue = numericValue > 1 ? numericValue / 100 : numericValue;
    if (normalizedValue < 0 || normalizedValue > 1) return null;
    return normalizedValue;
};

const thresholdToPercent = (value) => {
    const normalizedValue = normalizeThresholdValue(value);
    if (normalizedValue === null) return '';
    const percent = Number((normalizedValue * 100).toFixed(1));
    return Number.isInteger(percent) ? String(percent) : String(percent);
};

const percentToThreshold = (value) => {
    if (value === '') return null;
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return null;
    return Math.min(100, Math.max(0, numericValue)) / 100;
};

const getThresholdStat = (statsByAlgorithm, algorithmId, groupName, thresholdKey) => (
    statsByAlgorithm?.[algorithmId]?.[groupName]?.[thresholdKey] || null
);

const getThresholdStatTooltip = (stat) => {
    if (!stat || stat.suggested_threshold === null || stat.suggested_threshold === undefined) {
        return 'Sem média suficiente para sugerir threshold';
    }

    const sourceLabel = stat.source === 'camera' ? 'Base da câmera' : 'Base do algoritmo';
    const confirmed = stat.confirmed_count > 0
        ? `Confirmadas: ${thresholdToPercent(stat.confirmed_avg)}% (${stat.confirmed_count})`
        : 'Confirmadas: sem amostras';
    const rejected = stat.rejected_count > 0
        ? `Rejeitadas: ${thresholdToPercent(stat.rejected_avg)}% (${stat.rejected_count})`
        : 'Rejeitadas: sem amostras';

    return `${sourceLabel}. ${confirmed}. ${rejected}.`;
};

const upsertThreshold = (config, groupName, thresholdKey, value) => {
    const nextConfig = { ...(config || {}) };
    const nextGroup = { ...(nextConfig[groupName] || {}) };

    if (value === null || value === undefined || value === '') {
        delete nextGroup[thresholdKey];
    } else {
        nextGroup[thresholdKey] = value;
    }

    if (Object.keys(nextGroup).length === 0) {
        delete nextConfig[groupName];
    } else {
        nextConfig[groupName] = nextGroup;
    }

    return nextConfig;
};

const ConfidenceThresholdControl = memo(({ value, stat, onCommit, isDisabled = false }) => {
    const [draftValue, setDraftValue] = useState(thresholdToPercent(value));
    const hasSuggestedValue = stat?.suggested_threshold !== null && stat?.suggested_threshold !== undefined;
    const suggestedValue = hasSuggestedValue ? stat.suggested_threshold : DEFAULT_THRESHOLD;

    useEffect(() => {
        setDraftValue(thresholdToPercent(value));
    }, [value]);

    const commitValue = () => {
        onCommit(percentToThreshold(draftValue));
    };

    return (
        <HStack spacing={2} minW="170px" justify="flex-end">
            <InputGroup size="xs" width="82px">
                <Input
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    value={draftValue}
                    placeholder={thresholdToPercent(suggestedValue)}
                    onChange={(event) => setDraftValue(event.target.value)}
                    onBlur={commitValue}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') event.currentTarget.blur();
                    }}
                    isDisabled={isDisabled}
                    textAlign="right"
                />
                <InputRightAddon px={2}>%</InputRightAddon>
            </InputGroup>
            <Tooltip label={getThresholdStatTooltip(stat)} placement="top">
                <Badge colorScheme={stat?.source === 'camera' ? 'green' : 'gray'} variant="subtle" minW="62px" textAlign="center">
                    {hasSuggestedValue ? 'Base' : 'Padrão'} {thresholdToPercent(suggestedValue)}%
                </Badge>
            </Tooltip>
        </HStack>
    );
});

const DEFAULT_MAP_CENTER = [-23.55052, -46.633308];

const normalizeTrafficSegmentPoints = (points) => {
    if (!Array.isArray(points)) return [];
    return points
        .map((point) => {
            const latitude = Array.isArray(point) ? Number(point[0]) : Number(point?.latitude);
            const longitude = Array.isArray(point) ? Number(point[1]) : Number(point?.longitude);
            return Number.isFinite(latitude) && Number.isFinite(longitude) ? [latitude, longitude] : null;
        })
        .filter(Boolean);
};

const SegmentMapClickHandler = ({ onAddPoint }) => {
    useMapEvents({
        click(event) {
            onAddPoint([Number(event.latlng.lat.toFixed(8)), Number(event.latlng.lng.toFixed(8))]);
        }
    });

    return null;
};

const CameraLocationClickHandler = ({ onSelect }) => {
    useMapEvents({
        click(event) {
            onSelect({
                latitude: Number(event.latlng.lat.toFixed(8)),
                longitude: Number(event.latlng.lng.toFixed(8))
            });
        }
    });

    return null;
};

const CameraLocationPicker = ({ latitude, longitude, onChange }) => {
    const numericLatitude = Number(latitude);
    const numericLongitude = Number(longitude);
    const hasCoordinates = Number.isFinite(numericLatitude) && Number.isFinite(numericLongitude);
    const center = hasCoordinates ? [numericLatitude, numericLongitude] : DEFAULT_MAP_CENTER;

    const clearLocation = () => onChange({ latitude: '', longitude: '' });

    return (
        <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" overflow="hidden" bg="gray.50">
            <Flex align="center" justify="space-between" px={3} py={2} borderBottomWidth="1px" borderColor="gray.200" gap={2} wrap="wrap">
                <HStack spacing={2}>
                    <Badge colorScheme={hasCoordinates ? "green" : "gray"}>{hasCoordinates ? 'Local definido' : 'Sem coordenadas'}</Badge>
                    {hasCoordinates && (
                        <Text fontSize="xs" color="gray.600">
                            {numericLatitude.toFixed(6)}, {numericLongitude.toFixed(6)}
                        </Text>
                    )}
                </HStack>
                <Button size="xs" colorScheme="red" variant="outline" onClick={clearLocation} isDisabled={!hasCoordinates}>Limpar</Button>
            </Flex>
            <Box h="260px">
                <MapContainer key={`${center[0]}-${center[1]}`} center={center} zoom={hasCoordinates ? 17 : 12} style={{ width: "100%", height: "100%" }} preferCanvas>
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <CameraLocationClickHandler onSelect={onChange} />
                    {hasCoordinates && (
                        <CircleMarker
                            center={[numericLatitude, numericLongitude]}
                            radius={8}
                            pathOptions={{ color: "#2563eb", fillColor: "#2563eb", fillOpacity: 0.9, weight: 3 }}
                        />
                    )}
                </MapContainer>
            </Box>
        </Box>
    );
};

const StreetSegmentEditor = ({ camera, onChange }) => {
    const points = normalizeTrafficSegmentPoints(camera?.traffic_segment);
    const latitude = Number(camera?.latitude);
    const longitude = Number(camera?.longitude);
    const hasCameraCenter = Number.isFinite(latitude) && Number.isFinite(longitude);
    const center = hasCameraCenter ? [latitude, longitude] : points[0] || DEFAULT_MAP_CENTER;

    const addPoint = (point) => onChange([...points, point]);
    const undoPoint = () => onChange(points.slice(0, -1));
    const clearPoints = () => onChange([]);
    const addCameraPoint = () => {
        if (hasCameraCenter) addPoint([latitude, longitude]);
    };

    return (
        <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" overflow="hidden" bg="gray.50">
            <Flex align="center" justify="space-between" px={3} py={2} borderBottomWidth="1px" borderColor="gray.200" gap={2} wrap="wrap">
                <HStack spacing={2}>
                    <Badge colorScheme={points.length >= 2 ? "green" : "gray"}>{points.length} pontos</Badge>
                    {points.length >= 2 && <Text fontSize="xs" color="gray.600">Trecho manual ativo</Text>}
                </HStack>
                <HStack spacing={2}>
                    <Button size="xs" variant="outline" onClick={addCameraPoint} isDisabled={!hasCameraCenter}>Ponto da câmera</Button>
                    <Button size="xs" variant="outline" onClick={undoPoint} isDisabled={points.length === 0}>Desfazer</Button>
                    <Button size="xs" colorScheme="red" variant="outline" onClick={clearPoints} isDisabled={points.length === 0}>Limpar</Button>
                </HStack>
            </Flex>
            <Box h="300px">
                <MapContainer key={`${center[0]}-${center[1]}`} center={center} zoom={17} style={{ width: "100%", height: "100%" }} preferCanvas>
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <SegmentMapClickHandler onAddPoint={addPoint} />
                    {hasCameraCenter && (
                        <CircleMarker
                            center={[latitude, longitude]}
                            radius={7}
                            pathOptions={{ color: "#2563eb", fillColor: "#2563eb", fillOpacity: 0.9, weight: 2 }}
                        />
                    )}
                    {points.length > 1 && (
                        <Polyline
                            positions={points}
                            pathOptions={{ color: "#dc2626", weight: 10, opacity: 0.78, lineCap: "round", lineJoin: "round" }}
                        />
                    )}
                    {points.map((point, index) => (
                        <CircleMarker
                            key={`${point[0]}-${point[1]}-${index}`}
                            center={point}
                            radius={5}
                            pathOptions={{ color: "#ffffff", fillColor: "#dc2626", fillOpacity: 1, weight: 2 }}
                        />
                    ))}
                </MapContainer>
            </Box>
        </Box>
    );
};

// Animações para bolinhas de status (pulsante)
const pulseYellow = keyframes`
    0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.35); }
    70% { transform: scale(1.12); box-shadow: 0 0 0 8px rgba(245, 158, 11, 0); }
    100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(245, 158, 11, 0); }
`;

const pulseRed = keyframes`
    0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.35); }
    70% { transform: scale(1.12); box-shadow: 0 0 0 8px rgba(239, 68, 68, 0); }
    100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
`;

// Gerenciador global de requisições de imagens
const ImageRequestQueue = (() => {
    const queue = [];
    const active = new Set();
    const MAX_CONCURRENT = 2; // Reduzido de 3 para 2 para diminuir carga no backend

    const processQueue = () => {
        while (queue.length > 0 && active.size < MAX_CONCURRENT) {
            const request = queue.shift();
            active.add(request.id);

            request.fetch()
                .finally(() => {
                    active.delete(request.id);
                    processQueue();
                });
        }
    };

    return {
        add: (id, fetchFn, priority = 0) => {
            // Evitar duplicatas
            if (active.has(id) || queue.some(item => item.id === id)) return;

            // Adicionar com prioridade (menor número = maior prioridade)
            const request = { id, fetch: fetchFn, priority };

            // Inserir na posição correta baseado na prioridade
            const insertIndex = queue.findIndex(item => item.priority > priority);
            if (insertIndex === -1) {
                queue.push(request);
            } else {
                queue.splice(insertIndex, 0, request);
            }

            processQueue();
        },
        clear: () => {
            queue.length = 0;
        },
        getQueueSize: () => queue.length,
        getActiveCount: () => active.size
    };
})();

const normalizeAlgorithmKey = (value = '') => value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const getAlgorithmKey = (algorithm = {}) => {
    const rawKey = algorithm.key || algorithm.algorithm_key;
    if (rawKey) return normalizeAlgorithmKey(rawKey);

    const normalizedName = normalizeAlgorithmKey(algorithm.name || algorithm.algorithm_name || '');
    if (!normalizedName) return '';
    if (normalizedName === 'lpr' || normalizedName.includes('reconhecimento_de_placas') || normalizedName.includes('plate_recognition')) return 'lpr';
    if (normalizedName === 'vehicle_infraction' || normalizedName.includes('reconhecimento_de_infracoes') || normalizedName.includes('infracoes_ia')) return 'vehicle_infraction';
    if (normalizedName === 'bus_lane' || normalizedName.includes('faixa_de_onibus') || normalizedName.includes('monitoramento_de_faixa')) return 'bus_lane';
    if (normalizedName === 'alpr_infraction') return 'alpr_infraction';
    if (normalizedName.includes('face')) return 'face_recognition';
    if (normalizedName.includes('seatbelt') || normalizedName.includes('cinto')) return 'seatbelt';
    if (normalizedName.includes('helmet') || normalizedName.includes('capacete')) return 'helmet_detection';
    if (normalizedName.includes('phone') || normalizedName.includes('celular')) return 'cellphone_infraction';
    return normalizedName;
};

const getAlgorithmDisplayName = (algorithm) => getAlgorithmDisplayLabel(algorithm);

// Detectar tipo de serviço pelo nome do algoritmo
const getServiceType = (algorithm) => {
    if (!algorithm) return 'standard';
    const key = getAlgorithmKey(algorithm);
    if (key === 'vehicle_infraction') return 'ia_infractions';
    if (key === 'bus_lane') return 'bus_lane';
    if (key === 'face_recognition') return 'face_recognition';
    return 'standard';
};

// Obter ícone por tipo de serviço
const getServiceTypeIcon = (algorithm) => {
    const type = getServiceType(algorithm);
    switch (type) {
        case 'ia_infractions': return <FaRobot />;
        case 'bus_lane': return <FaBus />;
        case 'face_recognition': return <FaUser />;
        default: return null;
    }
};

// Config padrão para cada tipo de serviço
const getDefaultServiceConfig = (algorithm) => {
    const type = getServiceType(algorithm);
    switch (type) {
        case 'ia_infractions':
            return {
                enabled_infractions: INFRACTION_TYPES.map(type => type.id),
                infraction_direction: '',
                thresholds: buildDefaultThresholds(INFRACTION_TYPES),
                wrong_way_thresholds: buildDefaultThresholds(WRONG_WAY_THRESHOLD_TYPES)
            };
        case 'bus_lane':
            return {
                allowed_vehicles: ['carro', 'moto'],
                infraction_direction: '',
                ignore_whitelisted_plates: false,
                wrong_way_thresholds: buildDefaultThresholds(WRONG_WAY_THRESHOLD_TYPES)
            };
        case 'face_recognition':
            return { ...DEFAULT_FACE_SERVICE_CONFIG };
        default:
            return null;
    }
};

// Normaliza IDs legados de allowed_vehicles para os IDs atuais (carro/moto)
const normalizeBusLaneConfig = (config) => {
    if (!config?.allowed_vehicles) return config;
    const legacyMap = {
        car_front: 'carro',
        car_back: 'carro',
        moto_with_rearview: 'moto',
        moto_no_rearview: 'moto',
    };
    const normalized = config.allowed_vehicles.map(v => legacyMap[v] ?? v);
    // deduplicar
    return { ...config, allowed_vehicles: [...new Set(normalized)] };
};

const getRoiSourceDimensions = (roi) => {
    if (!roi) return null;
    const width = Number(roi.source_width ?? roi.sourceWidth);
    const height = Number(roi.source_height ?? roi.sourceHeight);
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
        return null;
    }
    return { width, height };
};

const roiConfigToDisplayPoints = (roi, targetWidth, targetHeight) => {
    const { x, y, width, height } = roi;
    if (x == null || y == null || width == null || height == null) return [];

    const source = getRoiSourceDimensions(roi);
    const scaleX = source ? targetWidth / source.width : 1;
    const scaleY = source ? targetHeight / source.height : 1;

    const rawPoints = Array.isArray(roi.points) && roi.points.length === 4
        ? roi.points
        : [
            { x, y },
            { x: x + width, y },
            { x: x + width, y: y + height },
            { x, y: y + height }
        ];

    return rawPoints.map((point) => ({
        x: Math.round(point.x * scaleX),
        y: Math.round(point.y * scaleY)
    }));
};

const buildCameraWithServices = (cameraData, services = []) => {
    const normalizedServices = services.map((service) => ({
        algorithm_id: service.algorithm_id,
        algorithm_key: service.algorithm_key,
        algorithm_name: service.algorithm_name,
        status: service.status,
        config: service.config
    }));
    const activeServices = normalizedServices.filter((service) => service.status);

    return {
        ...cameraData,
        services: normalizedServices,
        activeServices,
        isActive: activeServices.length > 0
    };
};

const AuthenticatedImage = memo(({ cameraId, alt, onRefresh, useLastImage = false, cameraIndex = 0, ...props }) => {
    const [imageUrl, setImageUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const hasFetchedRef = useRef(false);
    const imgContainerRef = useRef(null);
    const failedAttemptsRef = useRef(0);
    const isLoadingRef = useRef(false);
    const isMountedRef = useRef(true);
    const isVisibleRef = useRef(false);
    const MAX_RETRY_ATTEMPTS = 1;

    const fetchImage = useCallback(async () => {
        // Evitar múltiplas chamadas simultâneas ou excesso de tentativas
        if (isLoadingRef.current || !isMountedRef.current || (hasFetchedRef.current && failedAttemptsRef.current >= MAX_RETRY_ATTEMPTS)) {
            return;
        }

        isLoadingRef.current = true;
        setLoading(true);

        try {
            setError(false);

            // Escolher endpoint baseado no prop useLastImage
            const endpoint = useLastImage
                ? `/api/cameras/last-image/${cameraId}`
                : `/api/cameras/still-image/${cameraId}`;

            const token = localStorage.getItem('token');
            const response = await fetch(endpoint, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Cache-Control': 'public, max-age=600'
                }
            });

            if (!response.ok) {
                failedAttemptsRef.current += 1;
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();

            if (!isMountedRef.current) return;

            const url = URL.createObjectURL(blob);
            setImageUrl(url);
            setError(false);
            hasFetchedRef.current = true;
            failedAttemptsRef.current = 0;

        } catch (error) {
            if (!isMountedRef.current) return;
            console.error(`Error fetching camera ${cameraId} image (attempt ${failedAttemptsRef.current}):`, error.message);
            setError(true);
        } finally {
            isLoadingRef.current = false;
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    }, [cameraId, useLastImage]);

    // Função para refresh manual
    const handleRefresh = useCallback(() => {
        // Reset contadores para permitir nova tentativa manual
        hasFetchedRef.current = false;
        failedAttemptsRef.current = 0;
        isLoadingRef.current = false;
        setError(false);
        fetchImage();
    }, [fetchImage]);

    // IntersectionObserver: só carregar imagem quando o card está visível
    useEffect(() => {
        isMountedRef.current = true;
        const container = imgContainerRef.current;
        if (!container) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !isVisibleRef.current) {
                    isVisibleRef.current = true;
                    if (!hasFetchedRef.current && failedAttemptsRef.current < MAX_RETRY_ATTEMPTS) {
                        ImageRequestQueue.add(`camera-${cameraId}`, () => fetchImage(), cameraIndex);
                    }
                }
            },
            { rootMargin: '100px', threshold: 0.1 } // Começar a carregar 100px antes de ficar visível
        );

        observer.observe(container);

        return () => {
            isMountedRef.current = false;
            observer.disconnect();
        };
    }, [cameraId, cameraIndex, fetchImage]);

    // Registrar função de refresh apenas uma vez
    useEffect(() => {
        if (onRefresh) {
            onRefresh(handleRefresh);
        }
    }, [onRefresh, handleRefresh]);

    if (loading) {
        return (
            <Flex
                ref={imgContainerRef}
                justifyContent="center"
                alignItems="center"
                width="100%"
                height={props.height || "120px"}
                bg="gray.100"
                borderRadius="md"
            >
                <Spinner size="md" color="blue.500" />
            </Flex>
        );
    }

    if (error || !imageUrl) {
        return (
            <Box ref={imgContainerRef}>
                <Image
                    src={process.env.PUBLIC_URL + "/camera-offline.jpg"}
                    alt={alt || "Câmera offline"}
                    {...props}
                />
            </Box>
        );
    }

    return (
        <Box ref={imgContainerRef}>
            <Image src={imageUrl} alt={alt} {...props} />
        </Box>
    );
});

// ROI Selector com desenho de polígono de 4 pontos no canvas
const RoiSelector = memo(({ cameraId, onSave, onCancel }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [imageData, setImageData] = useState(null);
    const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
    const [imageLoaded, setImageLoaded] = useState(false);
    const [points, setPoints] = useState([]); // até 4 pontos em coordenadas naturais da imagem
    const [existingRoi, setExistingRoi] = useState(null);
    const imgRef = useRef(null);
    const canvasRef = useRef(null);
    const toast = useToast();

    // Buscar configuração ROI existente e carregar imagem
    useEffect(() => {
        const fetchExistingRoi = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`/api/cameras/roi/${cameraId}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    setExistingRoi(data);
                }
            } catch (error) {
                console.error('Erro ao buscar ROI existente:', error);
            }
        };

        const loadImage = async () => {
            try {
                setIsLoading(true);
                const token = localStorage.getItem('token');

                const response = await fetch(`/api/cameras/still-image/${cameraId}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (response.ok) {
                    const blob = await response.blob();
                    const imageUrl = URL.createObjectURL(blob);
                    setImageData(imageUrl);
                } else {
                    throw new Error('Falha ao carregar imagem');
                }
            } catch (error) {
                console.error('Erro ao carregar imagem:', error);
                toast({
                    title: 'Erro ao carregar imagem',
                    description: 'Não foi possível carregar a imagem da câmera',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
            } finally {
                setIsLoading(false);
            }
        };

        fetchExistingRoi();
        loadImage();

        return () => {
            if (imageData) {
                URL.revokeObjectURL(imageData);
            }
        };
    }, [cameraId]); // Apenas cameraId como dependência

    // Lidar com o carregamento da imagem
    const handleImageLoad = useCallback(() => {
        if (imgRef.current) {
            const { naturalWidth, naturalHeight } = imgRef.current;
            setImageSize({ width: naturalWidth, height: naturalHeight });
            setImageLoaded(true);
        }
    }, []);

    // Escalar ROI salvo para a resolução da imagem exibida (passagem vs infração Full HD)
    useEffect(() => {
        if (!existingRoi || !imageLoaded || imageSize.width <= 0 || imageSize.height <= 0) return;
        setPoints(roiConfigToDisplayPoints(existingRoi, imageSize.width, imageSize.height));
    }, [existingRoi, imageLoaded, imageSize.width, imageSize.height]);

    // Redraw canvas toda vez que os pontos ou o tamanho da imagem mudam
    useEffect(() => {
        if (!imageLoaded || !canvasRef.current || !imgRef.current) return;
        const canvas = canvasRef.current;
        const img = imgRef.current;

        // Ajustar canvas ao tamanho renderizado da imagem
        canvas.width = img.offsetWidth;
        canvas.height = img.offsetHeight;

        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (points.length === 0) return;

        const scaleX = canvas.width / imageSize.width;
        const scaleY = canvas.height / imageSize.height;
        const cp = points.map(p => ({ x: p.x * scaleX, y: p.y * scaleY }));

        // Polígono preenchido quando 4 pontos
        if (points.length === 4) {
            ctx.beginPath();
            ctx.moveTo(cp[0].x, cp[0].y);
            cp.slice(1).forEach(p => ctx.lineTo(p.x, p.y));
            ctx.closePath();
            ctx.fillStyle = 'rgba(56, 161, 105, 0.25)';
            ctx.fill();
            ctx.strokeStyle = '#38A169';
            ctx.lineWidth = 2;
            ctx.stroke();
        } else {
            // Linhas parciais
            ctx.beginPath();
            ctx.moveTo(cp[0].x, cp[0].y);
            cp.slice(1).forEach(p => ctx.lineTo(p.x, p.y));
            ctx.strokeStyle = '#38A169';
            ctx.lineWidth = 2;
            ctx.stroke();
        }

        // Marcadores numerados em cada ponto
        cp.forEach((p, i) => {
            ctx.beginPath();
            ctx.arc(p.x, p.y, 8, 0, Math.PI * 2);
            ctx.fillStyle = '#38A169';
            ctx.fill();
            ctx.strokeStyle = 'white';
            ctx.lineWidth = 2;
            ctx.stroke();

            ctx.fillStyle = 'white';
            ctx.font = 'bold 11px sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(i + 1, p.x, p.y);
        });
    }, [points, imageLoaded, imageSize]);

    // Clique no canvas → adiciona ponto em coordenadas naturais da imagem
    const handleCanvasClick = useCallback((e) => {
        if (points.length >= 4) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const cx = e.clientX - rect.left;
        const cy = e.clientY - rect.top;
        const ix = Math.round(cx * (imageSize.width / canvas.width));
        const iy = Math.round(cy * (imageSize.height / canvas.height));
        setPoints(prev => [...prev, { x: ix, y: iy }]);
    }, [points.length, imageSize]);

    // Remover o último ponto (undo)
    const handleUndoPoint = useCallback(() => {
        setPoints(prev => prev.slice(0, -1));
    }, []);

    const handleSave = useCallback(() => {
        if (points.length !== 4) return;
        const xs = points.map(p => p.x);
        const ys = points.map(p => p.y);
        const x = Math.min(...xs);
        const y = Math.min(...ys);
        const width = Math.max(...xs) - x;
        const height = Math.max(...ys) - y;

        onSave({
            x, y, width, height,
            sourceWidth: imageSize.width,
            sourceHeight: imageSize.height,
            points
        });
    }, [points, imageSize, onSave]);

    const handleReset = useCallback(() => {
        if (existingRoi && imageSize.width > 0 && imageSize.height > 0) {
            setPoints(roiConfigToDisplayPoints(existingRoi, imageSize.width, imageSize.height));
        } else {
            setPoints([]);
        }
    }, [existingRoi, imageSize.width, imageSize.height]);

    return (
        <Box>
            {isLoading ? (
                <Flex justifyContent="center" alignItems="center" height="400px">
                    <Spinner size="xl" thickness="4px" color="blue.500" />
                </Flex>
            ) : (
                <Box>
                    <VStack spacing={4} align="stretch">
                        {/* Instrução */}
                        <Box p={3} bg="blue.50" borderRadius="md" borderLeft="4px solid" borderColor="blue.400">
                            <Text fontSize="sm" color="blue.700" fontWeight="medium">
                                {points.length < 4
                                    ? `Clique na imagem para marcar o ponto ${points.length + 1} de 4`
                                    : 'Área selecionada. Clique em "Salvar ROI" para confirmar ou "Redefinir" para recomeçar.'}
                            </Text>
                        </Box>

                        {/* Canvas sobre a imagem */}
                        <Box
                            position="relative"
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="md"
                            overflow="hidden"
                            bg="gray.100"
                            userSelect="none"
                        >
                            {imageData && (
                                <>
                                    <Image
                                        ref={imgRef}
                                        src={imageData}
                                        alt="Imagem da câmera"
                                        width="100%"
                                        style={{ display: 'block' }}
                                        onLoad={handleImageLoad}
                                        draggable={false}
                                        onError={() => {
                                            toast({
                                                title: 'Erro na imagem',
                                                description: 'Não foi possível exibir a imagem',
                                                status: 'error',
                                                duration: 3000,
                                                isClosable: true,
                                            });
                                        }}
                                    />
                                    <canvas
                                        ref={canvasRef}
                                        onClick={handleCanvasClick}
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            cursor: points.length >= 4 ? 'default' : 'crosshair',
                                        }}
                                    />
                                </>
                            )}
                        </Box>

                        {/* Status dos pontos */}
                        {imageLoaded && (
                            <Flex gap={2} flexWrap="wrap" align="center">
                                {[1, 2, 3, 4].map(n => (
                                    <Badge
                                        key={n}
                                        colorScheme={points.length >= n ? 'green' : 'gray'}
                                        variant={points.length >= n ? 'solid' : 'outline'}
                                        px={3} py={1} borderRadius="full"
                                    >
                                        Ponto {n}{points[n - 1] ? `: (${points[n - 1].x}, ${points[n - 1].y})` : ''}
                                    </Badge>
                                ))}
                                {points.length > 0 && (
                                    <Button size="xs" variant="ghost" colorScheme="orange" onClick={handleUndoPoint} leftIcon={<FaUndo />}>
                                        Desfazer último
                                    </Button>
                                )}
                            </Flex>
                        )}

                        <Divider />

                        {/* Botões de ação */}
                        <HStack spacing={4} justify="flex-end">
                            <Button
                                colorScheme="blue"
                                onClick={handleSave}
                                isDisabled={points.length !== 4}
                            >
                                Salvar ROI
                            </Button>

                            <Button
                                variant="outline"
                                onClick={handleReset}
                                isDisabled={!imageLoaded}
                            >
                                Redefinir
                            </Button>

                            <Button
                                variant="ghost"
                                onClick={onCancel}
                            >
                                Cancelar
                            </Button>
                        </HStack>
                    </VStack>
                </Box>
            )}
        </Box>
    );
});

const CAMERAS_PER_PAGE = 12; // Paginação para evitar renderizar centenas de cards

const Cameras = () => {
    const [cameras, setCameras] = useState([]);
    const [filteredCameras, setFilteredCameras] = useState([]);
    const [machines, setMachines] = useState([]);
    const [selectedCamera, setSelectedCamera] = useState({ algorithms: [] });
    const [algorithms, setAlgorithms] = useState([]);

    const uniqueAlgorithms = useMemo(() => {
        const map = new Map();
        for (const alg of algorithms) {
            const key = getAlgorithmKey(alg) || String(alg.id);
            if (!map.has(key)) map.set(key, alg);
        }
        return Array.from(map.values());
    }, [algorithms]);
    const [loading, setLoading] = useState(true);
    // Estado para modal de thresholds globais
    const [isGlobalModalOpen, setIsGlobalModalOpen] = useState(false);
    const [globalThresholds, setGlobalThresholds] = useState(() => buildDefaultThresholds(INFRACTION_TYPES, DEFAULT_THRESHOLD));
    // Atualiza os thresholds globais ao carregar do backend
    useEffect(() => {
        const fetchGlobalThresholds = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch('/api/cameras/config/global-thresholds', {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (response.ok) {
                    const data = await response.json();
                    const merged = buildDefaultThresholds(INFRACTION_TYPES, DEFAULT_THRESHOLD);
                    for (const inf of INFRACTION_TYPES) {
                        if (data[inf.id] !== undefined) {
                            merged[inf.id] = data[inf.id];
                        }
                    }
                    setGlobalThresholds(merged);
                }
            } catch (error) {
                console.error("Erro ao buscar thresholds globais", error);
            }
        };
        fetchGlobalThresholds();
    }, []);
    // Função para abrir modal global
    const handleOpenGlobalModal = () => {
        setIsGlobalModalOpen(true);
    };
    const handleCloseGlobalModal = () => {
        setIsGlobalModalOpen(false);
    };

    // Função para salvar thresholds globais no backend
    const handleSaveGlobalThresholds = async (newThresholds) => {
        setGlobalThresholds(newThresholds);
        setIsGlobalModalOpen(false);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/cameras/config/global-thresholds', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(newThresholds)
            });

            if (!response.ok) throw new Error('Erro ao salvar configurações globais');

            toast({
                title: 'Thresholds globais atualizados',
                description: 'Os novos valores serão aplicados a todas as câmeras sem threshold específico.',
                status: 'success',
                duration: 5000,
                isClosable: true,
            });
            fetchCameras();
        } catch (error) {
            toast({
                title: 'Erro ao atualizar thresholds globais',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };
    const [refreshFunctions, setRefreshFunctions] = useState({});
    const [isRoiModalOpen, setIsRoiModalOpen] = useState(false);
    const [roiCamera, setRoiCamera] = useState(null);

    // Estado para armazenar metadados das imagens (carregados em batch)
    const [imageMetadata, setImageMetadata] = useState({});
    const [thresholdStats, setThresholdStats] = useState({});

    // Paginação
    const [currentPage, setCurrentPage] = useState(1);
    // Ordenação ('' | 'last_capture_desc' | 'last_capture_asc')
    const [sortBy, setSortBy] = useState('');

    // Estados para estatísticas
    const [statistics, setStatistics] = useState({
        total: 0,
        online: 0,
        offline: 0,
        totalActiveAlgorithms: 0
    });

    // Estados para filtros
    const [filters, setFilters] = useState({
        name: "",
        machineId: "",
        location: "",
        algorithmId: "",
        status: ""
    });

    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
    const { isOpen: isFilterOpen, onOpen: onFilterOpen, onClose: onFilterClose } = useDisclosure();

    const [newCamera, setNewCamera] = useState({
        name: '',
        location: '',
        rtsp_address: '',
        machine_id: '',
        latitude: '',
        longitude: '',
        traffic_segment: [],
        vehicle_types: [] // <-- ADICIONADO
    });
    const selectedMachineIdRef = useRef(null);
    const toast = useToast();

    const bgColor = useColorModeValue("white", "gray.800");
    const borderColor = useColorModeValue("gray.200", "gray.700");

    useEffect(() => {
        fetchCameras();
        fetchAlgorithms();
        fetchMachines();
    }, []);

    // Aplicar filtros e calcular estatísticas sempre que os dados mudarem
    useEffect(() => {
        applyFilters();
        calculateStatistics();
    }, [cameras, filters, imageMetadata, sortBy]);

    // Resetar paginação apenas quando filtros ou ordenação mudam
    useEffect(() => {
        setCurrentPage(1);
    }, [filters, sortBy]);

    const fetchCameras = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            // Usar novo endpoint otimizado
            const response = await fetch('/api/cameras/with-services', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Erro ao buscar câmeras');
            const data = await response.json();
            setCameras(data);

            // Buscar metadados em batch (1 requisição em vez de N)
            fetchBatchMetadata(token);
        } catch (error) {
            toast({
                title: "Erro ao buscar câmeras",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    const refreshCameraInList = useCallback(async (cameraId) => {
        try {
            const token = localStorage.getItem('token');
            const [cameraResponse, servicesResponse] = await Promise.all([
                fetch(`/api/cameras/${cameraId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch(`/api/camera-services/${cameraId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
            ]);

            if (!cameraResponse.ok) return null;

            const cameraData = await cameraResponse.json();
            const services = servicesResponse.ok ? await servicesResponse.json() : [];
            const mergedCamera = buildCameraWithServices(cameraData, services);

            setCameras((prev) => prev.map((camera) => (
                camera.id === cameraId ? mergedCamera : camera
            )));

            setSelectedCamera((prev) => {
                if (prev.id !== cameraId) return prev;

                const serviceConfigs = Object.fromEntries(
                    (mergedCamera.activeServices || [])
                        .filter((service) => service.config)
                        .map((service) => {
                            const type = getServiceType(
                                algorithms.find((algorithm) => algorithm.id === service.algorithm_id)
                                || { key: service.algorithm_key, name: service.algorithm_name }
                            );
                            const config = type === 'bus_lane'
                                ? normalizeBusLaneConfig(service.config)
                                : { ...service.config };
                            return [service.algorithm_id, config];
                        })
                );

                return {
                    ...mergedCamera,
                    algorithms: mergedCamera.activeServices?.map((service) => service.algorithm_id) || [],
                    serviceConfigs,
                    vehicle_types: mergedCamera.vehicle_types || prev.vehicle_types || [],
                    traffic_segment: normalizeTrafficSegmentPoints(mergedCamera.traffic_segment)
                };
            });

            return mergedCamera;
        } catch (error) {
            console.error('Erro ao atualizar câmera na lista:', error);
            return null;
        }
    }, [algorithms]);

    const patchCameraServiceConfigInList = useCallback((cameraId, algorithmId, newConfig) => {
        const patchServices = (services = []) => services.map((service) => (
            service.algorithm_id === algorithmId
                ? { ...service, config: newConfig }
                : service
        ));

        setCameras((prev) => prev.map((camera) => {
            if (camera.id !== cameraId) return camera;
            const services = patchServices(camera.services);
            const activeServices = patchServices(camera.activeServices);
            return { ...camera, services, activeServices };
        }));
    }, []);

    // Buscar metadados de todas as câmeras em uma única requisição
    const fetchBatchMetadata = async (token) => {
        try {
            const response = await fetch('/api/cameras/batch-image-metadata', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setImageMetadata(data);
            }
        } catch (error) {
            console.warn('Erro ao buscar metadados batch:', error);
        }
    };

    const handleOpenRoiModal = useCallback((camera) => {
        console.log('Abrindo modal ROI para câmera:', camera.id);
        setRoiCamera(camera);
        setIsRoiModalOpen(true);
    }, []);

    const handleCloseRoiModal = useCallback(() => {
        setIsRoiModalOpen(false);
        // Limpe o roiCamera apenas após o fechamento do modal
        setTimeout(() => {
            setRoiCamera(null);
        }, 300); // Dê tempo para a animação de fechamento terminar
    }, []);

    const handleSaveRoi = async (roi) => {
        if (!roiCamera) return;

        try {
            const token = localStorage.getItem('token');

            const response = await fetch(`/api/cameras/roi/${roiCamera.id}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(roi)
            });

            if (!response.ok) {
                throw new Error('Erro ao salvar configuração de ROI');
            }

            toast({
                title: "ROI configurado",
                description: "Área de interesse configurada com sucesso",
                status: "success",
                duration: 3000,
                isClosable: true,
            });

            handleCloseRoiModal();
        } catch (error) {
            console.error('Erro:', error);
            toast({
                title: "Erro",
                description: "Não foi possível configurar a área de interesse",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const fetchMachines = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/machines', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Erro ao buscar máquinas');
            const data = await response.json();
            setMachines(data);
            return data;
        } catch (error) {
            console.error('Erro:', error);
            return [];
        }
    };

    const fetchAlgorithms = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/algorithms', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Erro ao buscar algoritmos');
            const data = await response.json();
            const hydratedAlgorithms = data.map(algorithm => ({
                ...algorithm,
                key: getAlgorithmKey(algorithm)
            }));
            const visibleAlgorithms = filterAllowedAlgorithms(hydratedAlgorithms);
            setAlgorithms(visibleAlgorithms);
            return visibleAlgorithms;
        } catch (error) {
            console.error('Erro:', error);
            return [];
        }
    };

    const fetchThresholdStats = useCallback(async (cameraId, algorithmIds = []) => {
        if (!cameraId || algorithmIds.length === 0) {
            setThresholdStats({});
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const uniqueAlgorithmIds = [...new Set(algorithmIds.filter(Boolean))];
            const responses = await Promise.all(uniqueAlgorithmIds.map(async (algorithmId) => {
                const response = await fetch(`/api/camera-services/threshold-stats?camera_id=${cameraId}&algorithm_id=${algorithmId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!response.ok) throw new Error(`Erro ao buscar médias do algoritmo ${algorithmId}`);
                return [algorithmId, await response.json()];
            }));

            setThresholdStats(prev => ({ ...prev, ...Object.fromEntries(responses) }));
        } catch (error) {
            console.warn('Erro ao buscar médias de threshold:', error);
            setThresholdStats({});
        }
    }, []);

    // Função para calcular estatísticas
    const calculateStatistics = useCallback(() => {
        const total = cameras.length;
        const online = cameras.filter(camera => camera.isActive).length;
        const offline = total - online;

        // Somar total de algoritmos ativos
        const totalActiveAlgorithms = cameras.reduce((sum, camera) => {
            return sum + (camera.activeServices?.length || 0);
        }, 0);

        setStatistics({
            total,
            online,
            offline,
            totalActiveAlgorithms
        });
    }, [cameras]);

    // Função para aplicar filtros
    const applyFilters = () => {
        let result = [...cameras];

        // Filtro por nome da câmera
        if (filters.name) {
            result = result.filter(camera =>
                camera.name &&
                camera.name.toLowerCase().includes(filters.name.toLowerCase())
            );
        }

        // Filtro por máquina
        if (filters.machineId) {
            const machineIdNum = parseInt(filters.machineId, 10);
            result = result.filter(camera => camera.machine_id === machineIdNum);
        }

        // Filtro por localização (zona)
        if (filters.location) {
            result = result.filter(camera =>
                camera.location &&
                camera.location.toLowerCase().includes(filters.location.toLowerCase())
            );
        }

        // Filtro por status (ativa/inativa)
        if (filters.status) {
            if (filters.status === 'active') {
                result = result.filter(camera => camera.isActive);
            } else if (filters.status === 'inactive') {
                result = result.filter(camera => !camera.isActive);
            }
        }

        // Filtro por algoritmo ativo
        if (filters.algorithmId) {
            const algorithmIdNum = parseInt(filters.algorithmId, 10);
            result = result.filter(camera => {
                return camera.activeServices?.some(service => service.algorithm_id === algorithmIdNum);
            });
        }

        // Ordenação por última captura (se selecionado)
        if (sortBy === 'last_capture_desc' || sortBy === 'last_capture_asc') {
            const getTs = (camera) => {
                const meta = imageMetadata[camera.id];
                const ts = meta?.timestamp || camera.last_capture_time || camera.last_capture || null;
                return ts ? new Date(ts).getTime() : 0;
            };

            result.sort((a, b) => {
                const ta = getTs(a);
                const tb = getTs(b);
                return sortBy === 'last_capture_desc' ? tb - ta : ta - tb;
            });
        }

        setFilteredCameras(result);
    };

    // Função para lidar com mudanças nos filtros
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    // Função para resetar filtros
    const resetFilters = () => {
        setFilters({
            name: "",
            machineId: "",
            location: "",
            algorithmId: "",
            status: ""
        });
    };

    // Função para contar câmeras filtradas
    const getFilteredCount = () => {
        return filteredCameras.length;
    };

    // Função para verificar se há filtros ativos
    const hasActiveFilters = () => {
        return filters.name || filters.machineId || filters.location || filters.algorithmId || filters.status;
    };

    const handleOpenModal = () => {
        onOpen();
        fetchMachines();
    };

    const handleOpenEditModal = async (camera) => {
        try {
            // Usar exatamente a configuração salva no banco para cada serviço.
            // Assim, apenas infrações presentes em enabled_infractions ficam marcadas.
            const serviceConfigs = Object.fromEntries(
                (camera.activeServices || [])
                    .filter(s => s.config)
                    .map(s => {
                        const type = getServiceType(algorithms.find(a => a.id === s.algorithm_id) || { key: s.algorithm_key, name: s.algorithm_name });
                        const config = type === 'bus_lane' ? normalizeBusLaneConfig(s.config) : { ...s.config };
                        return [s.algorithm_id, config];
                    })
            );

            setSelectedCamera({
                ...camera,
                algorithms: camera.activeServices?.map(s => s.algorithm_id) || [],
                serviceConfigs,
                vehicle_types: camera.vehicle_types || [],
                traffic_segment: normalizeTrafficSegmentPoints(camera.traffic_segment)
            });
            selectedMachineIdRef.current = camera.machine_id ?? null;
            setThresholdStats({});

            await Promise.all([fetchAlgorithms(), fetchMachines()]);
            await fetchThresholdStats(camera.id, camera.activeServices?.map(service => service.algorithm_id) || []);

            onEditOpen();
        } catch (error) {
            console.error('Erro ao abrir o modal de edição:', error);
        }
    };

    const handleNewCameraInputChange = (e) => {
        const { name, value } = e.target;
        const parsedValue = name === 'machine_id' ? (value === '' ? '' : Number(value)) : value;
        setNewCamera(prev => ({ ...prev, [name]: parsedValue }));
    };

    // Novo: toggle para tipos de veículos no modal de adicionar
    const handleNewCameraToggleVehicleType = (typeId) => {
        setNewCamera(prev => {
            const current = new Set(prev.vehicle_types || []);
            if (current.has(typeId)) current.delete(typeId);
            else current.add(typeId);
            return { ...prev, vehicle_types: Array.from(current) };
        });
    };

    const handleSelectedCameraInputChange = (e) => {
        const { name, value } = e.target;
        const parsedValue = name === 'machine_id'
            ? (value === '' ? null : Number(value))
            : value;
        if (name === 'machine_id') {
            selectedMachineIdRef.current = parsedValue;
        }
        setSelectedCamera(prev => ({ ...prev, [name]: parsedValue }));
    };

    const handleAddCamera = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/cameras', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(newCamera),
            });

            if (!response.ok) {
                throw new Error('Erro ao adicionar câmera');
            }

            const addedCamera = await response.json();
            setCameras(prev => [...prev, addedCamera]);
            setNewCamera({ name: '', location: '', rtsp_address: '', machine_id: '', latitude: '', longitude: '', traffic_segment: [], vehicle_types: [] });
            onClose();

            toast({
                title: "Câmera adicionada",
                description: "A nova câmera foi adicionada com sucesso",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error('Erro:', error);
            toast({
                title: "Erro",
                description: "Não foi possível adicionar a câmera",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleUpdateCamera = async () => {
        try {
            const token = localStorage.getItem('token');
            const originalMachineId = cameras.find(c => c.id === selectedCamera.id)?.machine_id;
            const nextMachineId = selectedMachineIdRef.current ?? selectedCamera?.machine_id ?? null;
            const normalizedCamera = {
                ...selectedCamera,
                machine_id: nextMachineId === '' ? null : (nextMachineId == null ? null : Number(nextMachineId))
            };

            const cameraResponse = await fetch(`/api/cameras/${selectedCamera.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(normalizedCamera),
            });

            if (!cameraResponse.ok) {
                throw new Error(`Erro ao atualizar câmera: ${cameraResponse.statusText}`);
            }

            const updatedCamera = await cameraResponse.json();

            if (originalMachineId !== normalizedCamera.machine_id) {
                const servicesResponse = await fetch(`/api/camera-services/${selectedCamera.id}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (!servicesResponse.ok) {
                    throw new Error('Erro ao buscar serviços da câmera');
                }

                const services = await servicesResponse.json();

                for (const service of services) {
                    const serviceResponse = await fetch(`/api/camera-services/${service.id}`, {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            camera_id: service.camera_id,
                            machine_id: normalizedCamera.machine_id,
                            algorithm_id: service.algorithm_id,
                            status: service.status
                        })
                    });

                    if (!serviceResponse.ok) {
                        const errorData = await serviceResponse.json();
                        throw new Error(`Erro ao atualizar serviço: ${errorData.details || errorData.message}`);
                    }
                }
            }

            await refreshCameraInList(selectedCamera.id);

            toast({
                title: "Sucesso",
                description: "Câmera atualizada com sucesso",
                status: "success",
                duration: 3000,
                isClosable: true,
            });

            onEditClose();
        } catch (error) {
            console.error('Erro:', error);
            toast({
                title: "Erro",
                description: "Não foi possível atualizar a câmera",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleAlgorithmToggle = async (algorithmKey) => {
        const group = algorithms.filter(a => getAlgorithmKey(a) === algorithmKey);
        if (!group.length) return;
        const activeIdsSet = new Set(selectedCamera.algorithms || []);
        const isActive = group.some(a => activeIdsSet.has(a.id));
        try {
            const token = localStorage.getItem('token');

            if (isActive) {
                for (const alg of group) {
                    try {
                        const serviceResponse = await fetch(`/api/camera-services/find`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({
                                camera_id: selectedCamera.id,
                                algorithm_id: alg.id,
                                algorithm_key: algorithmKey
                            })
                        });

                        if (!serviceResponse.ok) continue;
                        const serviceData = await serviceResponse.json();

                        const response = await fetch(`/api/camera-services/${serviceData.id}`, {
                            method: 'DELETE',
                            headers: {
                                'Authorization': `Bearer ${token}`
                            }
                        });

                        if (!response.ok) throw new Error('Erro ao desativar algoritmo');
                    } catch (err) {
                        console.warn('Erro ao desativar serviço para algoritmo', alg.id, err);
                    }
                }

                setSelectedCamera((prev) => ({
                    ...prev,
                    algorithms: (prev.algorithms || []).filter(id => !group.some(a => a.id === id)),
                    serviceConfigs: (() => { const c = { ...(prev.serviceConfigs || {}) }; for (const a of group) delete c[a.id]; return c; })()
                }));
            } else {
                const algorithmToActivate = group[0];
                const defaultConfig = getDefaultServiceConfig(algorithmToActivate);

                const response = await fetch('/api/camera-services', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        camera_id: selectedCamera.id,
                        machine_id: selectedCamera.machine_id,
                        algorithm_id: algorithmToActivate.id,
                        algorithm_key: algorithmKey,
                        status: true,
                        config: defaultConfig
                    }),
                });

                if (!response.ok) throw new Error('Erro ao ativar algoritmo');

                setSelectedCamera((prev) => ({
                    ...prev,
                    algorithms: [...(prev.algorithms || []).filter(id => !group.some(a => a.id === id)), algorithmToActivate.id],
                    serviceConfigs: { ...(prev.serviceConfigs || {}), [algorithmToActivate.id]: getDefaultServiceConfig(algorithmToActivate) }
                }));

                fetchThresholdStats(selectedCamera.id, [algorithmToActivate.id]);
            }

            await refreshCameraInList(selectedCamera.id);
        } catch (error) {
            console.error('Erro:', error);
            toast({
                title: "Erro",
                description: "Não foi possível atualizar o algoritmo",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    // Atualizar configuração de um serviço ativo
    const handleServiceConfigChange = async (algorithmId, newConfig) => {
        const algorithm = algorithms.find(a => a.id === algorithmId);
        try {
            const token = localStorage.getItem('token');

            // Buscar o serviço
            const serviceResponse = await fetch(`/api/camera-services/find`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    camera_id: selectedCamera.id,
                    algorithm_id: algorithmId,
                    algorithm_key: getAlgorithmKey(algorithm)
                })
            });

            if (!serviceResponse.ok) throw new Error('Serviço não encontrado');
            const serviceData = await serviceResponse.json();

            // Atualizar config
            const response = await fetch(`/api/camera-services/${serviceData.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ config: newConfig })
            });

            if (!response.ok) throw new Error('Erro ao atualizar configuração');

            // Atualizar estado local
            setSelectedCamera(prev => ({
                ...prev,
                serviceConfigs: { ...prev.serviceConfigs, [algorithmId]: newConfig }
            }));
            patchCameraServiceConfigInList(selectedCamera.id, algorithmId, newConfig);
        } catch (error) {
            console.error('Erro:', error);
            toast({
                title: "Erro",
                description: "Não foi possível atualizar a configuração do serviço",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    // Toggle de tipo de infração na config de "Reconhecimento de Infrações (IA)"
    const handleToggleInfractionType = (algorithmId, infractionId) => {
        const currentConfig = selectedCamera.serviceConfigs?.[algorithmId] || { enabled_infractions: [] };
        const list = [...(currentConfig.enabled_infractions || [])];
        const idx = list.indexOf(infractionId);
        if (idx >= 0) list.splice(idx, 1);
        else list.push(infractionId);
        handleServiceConfigChange(algorithmId, { ...currentConfig, enabled_infractions: list });
    };

    // Toggle de tipo de veículo na config de "Faixa de Ônibus"
    const handleToggleBusLaneVehicle = (algorithmId, vehicleId) => {
        const currentConfig = selectedCamera.serviceConfigs?.[algorithmId] || { allowed_vehicles: [], infraction_direction: '' };
        const list = [...(currentConfig.allowed_vehicles || [])];
        const idx = list.indexOf(vehicleId);
        if (idx >= 0) list.splice(idx, 1);
        else list.push(vehicleId);
        handleServiceConfigChange(algorithmId, { ...currentConfig, allowed_vehicles: list });
    };

    // Toggle de ignorar placas cadastradas na config de "Faixa de Ônibus"
    const handleToggleBusLaneIgnoreWhitelist = (algorithmId) => {
        const currentConfig = selectedCamera.serviceConfigs?.[algorithmId] || { allowed_vehicles: [], infraction_direction: '', ignore_whitelisted_plates: false };
        handleServiceConfigChange(algorithmId, { ...currentConfig, ignore_whitelisted_plates: !currentConfig.ignore_whitelisted_plates });
    };

    // Alterar direção de infração na config de "Faixa de Ônibus"
    const handleBusLaneDirectionChange = (algorithmId, direction) => {
        const currentConfig = selectedCamera.serviceConfigs?.[algorithmId] || { allowed_vehicles: [], infraction_direction: '' };
        handleServiceConfigChange(algorithmId, { ...currentConfig, infraction_direction: direction });
    };

    // Alterar direção de contramão na config de "Reconhecimento de Infrações (IA)"
    const handleIADirectionChange = (algorithmId, direction) => {
        const currentConfig = selectedCamera.serviceConfigs?.[algorithmId] || { enabled_infractions: [], infraction_direction: '' };
        handleServiceConfigChange(algorithmId, { ...currentConfig, infraction_direction: direction });
    };

    const handleInfractionThresholdChange = (algorithmId, infractionId, threshold) => {
        const currentConfig = selectedCamera.serviceConfigs?.[algorithmId] || {};
        handleServiceConfigChange(algorithmId, upsertThreshold(currentConfig, 'thresholds', infractionId, threshold));
    };

    const handleWrongWayThresholdChange = (algorithmId, classId, threshold) => {
        const currentConfig = selectedCamera.serviceConfigs?.[algorithmId] || {};
        handleServiceConfigChange(algorithmId, upsertThreshold(currentConfig, 'wrong_way_thresholds', classId, threshold));
    };

    const handleFaceThresholdChange = (algorithmId, field, threshold) => {
        const currentConfig = selectedCamera.serviceConfigs?.[algorithmId] || { ...DEFAULT_FACE_SERVICE_CONFIG };
        handleServiceConfigChange(algorithmId, { ...currentConfig, [field]: threshold });
    };

    const handleDeleteCamera = async (cameraId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/cameras/${cameraId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) throw new Error('Erro ao excluir câmera');

            setCameras(cameras.filter(camera => camera.id !== cameraId));

            toast({
                title: "Câmera excluída",
                description: "A câmera foi excluída com sucesso",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error('Erro:', error);
            toast({
                title: "Erro",
                description: "Não foi possível excluir a câmera",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    // Função para obter o nome da máquina pelo ID
    const getMachineName = (machineId) => {
        const machine = machines.find(m => m.id === machineId);
        return machine ? machine.name : 'Máquina não encontrada';
    };

    // Função para obter o nome do algoritmo pelo ID
    const getAlgorithmName = (algorithmId) => {
        const algorithm = algorithms.find(a => a.id === algorithmId);
        return getAlgorithmDisplayName(algorithm);
    };

    const getAlgorithmIcon = (algorithmId) => {
        // Primeiro verificar por tipo de serviço (para algoritmos novos)
        const algorithm = algorithms.find(a => a.id === algorithmId);
        const typeIcon = getServiceTypeIcon(algorithm);
        if (typeIcon) return typeIcon;

        const key = getAlgorithmKey(algorithm);
        if (key === 'lpr') return <FaCar />;
        if (key === 'alpr_infraction') return <FaBan />;
        if (key === 'face_recognition') return <FaUser />;
        if (key === 'cellphone_infraction') return <FaMobileAlt />;
        if (key === 'helmet_detection') return <FaHardHat />;
        if (key === 'seatbelt') return <FaSearch />;

        switch (algorithmId) {
            case 1: return <FaUser />;
            case 2: return <FaCar />;
            case 3: return <FaSearch />;
            case 4: return <FaMobileAlt />;
            case 5: return <FaHardHat />;
            case 6: return <FaBan />;
            default: return <FaSearch />;
        }
    };

    // Usar ref para evitar re-renders desnecessários
    const refreshFunctionsRef = useRef({});

    const handleSetRefreshFunction = useCallback((cameraId, refreshFn) => {
        refreshFunctionsRef.current[cameraId] = refreshFn;
        setRefreshFunctions(prev => ({
            ...prev,
            [cameraId]: refreshFn
        }));
    }, []);

    // Função para refresh manual
    const refreshCameraImage = useCallback((cameraId) => {
        if (refreshFunctions[cameraId]) {
            refreshFunctions[cameraId]();
        }
    }, [refreshFunctions]);

    return (
        <Box p={6} flex="1" minH="100vh" bg="gray.100" justify="center">
            <Box mb={6}>
                <Box display={{ base: "none", md: "block" }}>
                    <Flex justify="space-between" align="center" gap={3}>
                        <Heading>Câmeras</Heading>
                        <HStack spacing={3} wrap="wrap" justify="flex-end">
                            <Tooltip label="Configurações Globais de Thresholds" placement="top">
                                <IconButton
                                    icon={<SettingsIcon />}
                                    colorScheme="gray"
                                    variant="outline"
                                    aria-label="Configurações globais"
                                    onClick={handleOpenGlobalModal}
                                />
                            </Tooltip>
                            <Button
                                onClick={onFilterOpen}
                                colorScheme="blue"
                                size="md"
                                leftIcon={<FaFilter />}
                                variant={hasActiveFilters() ? "solid" : "outline"}
                            >
                                Filtros
                                {hasActiveFilters() && (
                                    <Badge ml={2} colorScheme="white" color="blue.100">
                                        {Object.values(filters).filter(Boolean).length}
                                    </Badge>
                                )}
                            </Button>
                            <Select
                                placeholder="Ordenar"
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                width="240px"
                                size="md"
                            >
                                <option value="last_capture_desc">Última captura (mais recentes)</option>
                                <option value="last_capture_asc">Última captura (mais antigas)</option>
                            </Select>
                            <Button
                                onClick={handleOpenModal}
                                colorScheme="teal"
                                size="md"
                                leftIcon={<AddIcon />}
                            >
                                Adicionar Câmera
                            </Button>
                        </HStack>
                    </Flex>
                </Box>

                <Box display={{ base: "block", md: "none" }}>
                    <Heading mb={3}>Câmeras</Heading>

                    <VStack spacing={2} align="stretch">
                        <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2}>
                            <Button
                                onClick={onFilterOpen}
                                colorScheme="blue"
                                size="md"
                                leftIcon={<FaFilter />}
                                variant={hasActiveFilters() ? "solid" : "outline"}
                            >
                                Filtros
                                {hasActiveFilters() && (
                                    <Badge ml={2} colorScheme="white" color="blue.100">
                                        {Object.values(filters).filter(Boolean).length}
                                    </Badge>
                                )}
                            </Button>

                            <Button
                                onClick={handleOpenModal}
                                colorScheme="teal"
                                size="md"
                                leftIcon={<AddIcon />}
                            >
                                Adicionar Câmera
                            </Button>
                        </SimpleGrid>

                        <Flex direction={{ base: "column", sm: "row" }} gap={2}>
                            <Select
                                placeholder="Ordenar"
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                size="md"
                                flex="1"
                            >
                                <option value="last_capture_desc">Última captura (mais recentes)</option>
                                <option value="last_capture_asc">Última captura (mais antigas)</option>
                            </Select>

                            <Tooltip label="Configurações Globais de Thresholds" placement="top">
                                <Button
                                    leftIcon={<SettingsIcon />}
                                    colorScheme="gray"
                                    variant="outline"
                                    size="md"
                                    onClick={handleOpenGlobalModal}
                                    width={{ base: "100%", sm: "auto" }}
                                >
                                    Thresholds
                                </Button>
                            </Tooltip>
                        </Flex>
                    </VStack>
                </Box>

                {/* Modal de configurações globais */}
                <GlobalThresholdsModal
                    isOpen={isGlobalModalOpen}
                    onClose={handleCloseGlobalModal}
                    defaultThresholds={globalThresholds}
                    onSave={handleSaveGlobalThresholds}
                />

                {/* Estatísticas */}
                <HStack
                    spacing={3}
                    justify={{ base: "center", md: "flex-start" }}
                    align="center"
                    bg="white"
                    p={3}
                    w="fit-content"
                    maxW="100%"
                    mx={{ base: "auto", md: "0" }}
                    borderRadius="md"
                    boxShadow="sm"
                    mt={4}
                    overflowX="auto"
                    whiteSpace="nowrap"
                >
                    <Tooltip label="Total de câmeras" placement="top">
                        <HStack spacing={1}>
                            <Badge colorScheme="blue" fontSize="md" px={2} py={1} display="inline-flex" alignItems="center" gap={1}>
                                <FaVideo /> {statistics.total}
                            </Badge>
                        </HStack>
                    </Tooltip>

                    <Divider orientation="vertical" height="20px" />

                    <Tooltip label="Câmeras online (com algoritmos ativos)" placement="top">
                        <HStack spacing={1}>
                            <Badge colorScheme="green" fontSize="md" px={2} py={1} display="inline-flex" alignItems="center" gap={1}>
                                <FaCheckCircle /> {statistics.online}
                            </Badge>
                        </HStack>
                    </Tooltip>

                    <Tooltip label="Câmeras offline (sem algoritmos ativos)" placement="top">
                        <HStack spacing={1}>
                            <Badge colorScheme="red" fontSize="md" px={2} py={1} display="inline-flex" alignItems="center" gap={1}>
                                <FaTimesCircle /> {statistics.offline}
                            </Badge>
                        </HStack>
                    </Tooltip>

                    <Divider orientation="vertical" height="20px" />

                    <Tooltip label="Total de algoritmos ativos em todas as câmeras" placement="top">
                        <HStack spacing={1}>
                            <Badge colorScheme="purple" fontSize="md" px={2} py={1} display="inline-flex" alignItems="center" gap={1}>
                                <FaRobot /> {statistics.totalActiveAlgorithms}
                            </Badge>
                        </HStack>
                    </Tooltip>
                </HStack>
            </Box>

            {/* Informações sobre filtros aplicados */}
            {hasActiveFilters() && (
                <Box mb={4} p={4} bg="blue.50" borderRadius="md" borderWidth="1px" borderColor="blue.200">
                    <Flex justify="space-between" align="center">
                        <HStack spacing={4} wrap="wrap">
                            <Text fontWeight="medium" color="blue.700">
                                Filtros ativos:
                            </Text>
                            {filters.name && (
                                <Tag colorScheme="blue" size="sm">
                                    Nome: {filters.name}
                                </Tag>
                            )}
                            {filters.machineId && (
                                <Tag colorScheme="blue" size="sm">
                                    Máquina: {getMachineName(parseInt(filters.machineId))}
                                </Tag>
                            )}
                            {filters.location && (
                                <Tag colorScheme="blue" size="sm">
                                    Zona: {filters.location}
                                </Tag>
                            )}
                            {filters.algorithmId && (
                                <Tag colorScheme="blue" size="sm">
                                    Algoritmo: {getAlgorithmName(parseInt(filters.algorithmId))}
                                </Tag>
                            )}
                            {filters.status && (
                                <Tag colorScheme="blue" size="sm">
                                    Status: {filters.status === 'active' ? 'Ativas' : filters.status === 'inactive' ? 'Inativas' : 'Todas'}
                                </Tag>
                            )}
                        </HStack>
                        <HStack>
                            <Text fontSize="sm" color="blue.600">
                                {getFilteredCount()} de {cameras.length} câmeras
                            </Text>
                            <Button size="sm" variant="ghost" colorScheme="blue" onClick={resetFilters}>
                                Limpar
                            </Button>
                        </HStack>
                    </Flex>
                </Box>
            )}

            {loading ? (
                <Flex justify="center" align="center" height="300px">
                    <Spinner size="xl" thickness="4px" speed="0.65s" color="teal.500" />
                </Flex>
            ) : filteredCameras.length === 0 ? (
                <Flex direction="column" align="center" justify="center" py={12} bg="white" borderRadius="md" boxShadow="sm">
                    <Text fontSize="xl" mb={4}>
                        {hasActiveFilters() ? 'Nenhuma câmera encontrada com os filtros aplicados' : 'Nenhuma câmera cadastrada'}
                    </Text>
                    {hasActiveFilters() ? (
                        <Button colorScheme="blue" onClick={resetFilters}>Limpar Filtros</Button>
                    ) : (
                        <Button colorScheme="teal" onClick={handleOpenModal}>Adicionar Primeira Câmera</Button>
                    )}
                </Flex>
            ) : (
                <>
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={6}>
                        {(() => {
                            const totalPages = Math.ceil(filteredCameras.length / CAMERAS_PER_PAGE);
                            const startIndex = (currentPage - 1) * CAMERAS_PER_PAGE;
                            const paginatedCameras = filteredCameras.slice(startIndex, startIndex + CAMERAS_PER_PAGE);
                            return paginatedCameras;
                        })().map((camera, index) => {
                            const isActive = camera.isActive;
                            const activeServices = camera.activeServices || [];
                            const metadata = imageMetadata[camera.id];
                            const latitude = Number(camera.latitude);
                            const longitude = Number(camera.longitude);
                            const hasCoordinates = Number.isFinite(latitude) && Number.isFinite(longitude);
                            const hasTrafficSegment = normalizeTrafficSegmentPoints(camera.traffic_segment).length >= 2;

                            // Determinar última captura e status baseado nela
                            const lastCaptureRaw = metadata?.timestamp || camera.last_capture_time || camera.last_capture || null;
                            const lastCaptureDate = lastCaptureRaw ? new Date(lastCaptureRaw) : null;
                            let statusLabel = 'Offline';
                            let statusTextColor = 'red.600';
                            let dotBg = 'red.500';
                            let dotAnim = undefined;

                            if (lastCaptureDate) {
                                const hoursAgo = (Date.now() - lastCaptureDate.getTime()) / (1000 * 60 * 60);
                                if (hoursAgo <= 1) {
                                    statusLabel = 'Online';
                                    statusTextColor = 'green.600';
                                    dotBg = 'green.400';
                                    dotAnim = undefined;
                                } else if (hoursAgo <= 6) {
                                    statusLabel = 'Atrasada';
                                    statusTextColor = 'yellow.700';
                                    dotBg = 'yellow.400';
                                    dotAnim = `${pulseYellow} 1.6s infinite`;
                                } else {
                                    statusLabel = 'Offline';
                                    statusTextColor = 'red.600';
                                    dotBg = 'red.500';
                                    dotAnim = `${pulseRed} 1.6s infinite`;
                                }
                            } else {
                                // Sem registro -> tratar como Offline
                                statusLabel = 'Offline';
                                statusTextColor = 'red.600';
                                dotBg = 'red.500';
                                dotAnim = `${pulseRed} 1.6s infinite`;
                            }

                            return (
                                <Box
                                    key={camera.id}
                                    bg={bgColor}
                                    p={4}
                                    borderRadius="md"
                                    boxShadow="md"
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                    position="relative"
                                    _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                                    transition="all 0.2s"
                                >
                                    {/* Status Badge (mostrar apenas quando ativa, em azul) */}
                                    {isActive && (
                                        <Badge
                                            position="absolute"
                                            top={2}
                                            right={2}
                                            colorScheme="blue"
                                            fontSize="xs"
                                            px={2}
                                            py={1}
                                            borderRadius="full"
                                            zIndex={1}
                                        >
                                            Ativa
                                        </Badge>
                                    )}

                                    {/* Camera Image */}
                                    <Box mb={4} position="relative">
                                        <AuthenticatedImage
                                            cameraId={camera.id}
                                            cameraIndex={index}
                                            alt={`Câmera ${camera.name}`}
                                            width="100%"
                                            height="130px"
                                            objectFit="cover"
                                            borderRadius="md"
                                            useLastImage={true}
                                            onRefresh={(refreshFn) => handleSetRefreshFunction(camera.id, refreshFn)}
                                        />
                                        {/* Refresh Button */}
                                        <IconButton
                                            icon={<RepeatIcon />}
                                            size="sm"
                                            position="absolute"
                                            bottom={2}
                                            right={2}
                                            colorScheme="blue"
                                            variant="solid"
                                            onClick={() => refreshCameraImage(camera.id)}
                                            aria-label="Atualizar imagem"
                                            opacity={0.8}
                                            _hover={{ opacity: 1 }}
                                        />
                                    </Box>

                                    {/* Camera Info */}
                                    <VStack align="start" spacing={2}>
                                        <Heading size="sm" noOfLines={1}>
                                            {camera.name}
                                        </Heading>

                                        <Text fontSize="sm" color="gray.600" noOfLines={2}>
                                            {camera.location}
                                        </Text>

                                        {hasCoordinates && (
                                            <HStack spacing={1} color="teal.600">
                                                <FaMapMarkerAlt size="12px" />
                                                <Text fontSize="xs">
                                                    {latitude.toFixed(6)}, {longitude.toFixed(6)}
                                                </Text>
                                            </HStack>
                                        )}

                                        {hasTrafficSegment && (
                                            <Badge colorScheme="red" variant="subtle">Trecho de fluxo manual</Badge>
                                        )}

                                        <Text fontSize="xs" color="gray.500">
                                            Máquina: {getMachineName(camera.machine_id)}
                                        </Text>

                                        {/* Última captura timestamp */}
                                        <Text fontSize="xs" color="gray.500">
                                            Última captura: {
                                                metadata?.timestamp ?
                                                    new Date(metadata.timestamp).toLocaleString('pt-BR', {
                                                        day: '2-digit',
                                                        month: '2-digit',
                                                        year: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                        second: '2-digit'
                                                    }) :
                                                    camera.last_capture_time ?
                                                        new Date(camera.last_capture_time).toLocaleString('pt-BR', {
                                                            day: '2-digit',
                                                            month: '2-digit',
                                                            year: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                            second: '2-digit'
                                                        }) :
                                                        'Sem registro'
                                            }
                                        </Text>

                                        {/* Indicador de status baseado na última captura */}
                                        <HStack spacing={2}>
                                            <Box w="10px" h="10px" borderRadius="full" bg={dotBg} animation={dotAnim} />
                                            <Text fontSize="xs" fontWeight="semibold" color={statusTextColor}>
                                                {statusLabel}
                                            </Text>
                                        </HStack>

                                        {/* Active Services */}
                                        {activeServices.length > 0 && (
                                            <HStack spacing={1} flexWrap="wrap">
                                                {activeServices.map(service => (
                                                    <Badge
                                                        key={service.algorithm_id}
                                                        colorScheme="blue"
                                                        fontSize="xs"
                                                        px={2}
                                                        py={1}
                                                        display="inline-flex"
                                                        alignItems="center"
                                                        gap={1}
                                                    >
                                                        {getAlgorithmIcon(service.algorithm_id)} {getAlgorithmName(service.algorithm_id)}
                                                    </Badge>
                                                ))}
                                            </HStack>
                                        )}
                                    </VStack>

                                    {/* Action Buttons */}
                                    <HStack spacing={2} mt={4}>
                                        <IconButton
                                            icon={<EditIcon />}
                                            size="sm"
                                            colorScheme="blue"
                                            variant="outline"
                                            onClick={() => handleOpenEditModal(camera)}
                                            aria-label="Editar câmera"
                                        />

                                        <IconButton
                                            icon={<SettingsIcon />}
                                            size="sm"
                                            colorScheme="green"
                                            variant="outline"
                                            onClick={() => handleOpenRoiModal(camera)}
                                            aria-label="Configurar ROI"
                                        />

                                        <IconButton
                                            icon={<DeleteIcon />}
                                            size="sm"
                                            colorScheme="red"
                                            variant="outline"
                                            onClick={() => handleDeleteCamera(camera.id)}
                                            aria-label="Deletar câmera"
                                        />
                                    </HStack>
                                </Box>
                            );
                        })}
                    </SimpleGrid>

                    {/* Controles de Paginação */}
                    {filteredCameras.length > CAMERAS_PER_PAGE && (() => {
                        const totalPages = Math.ceil(filteredCameras.length / CAMERAS_PER_PAGE);
                        return (
                            <Flex justify="center" align="center" mt={6} gap={2}>
                                <IconButton
                                    icon={<ChevronLeftIcon />}
                                    size="sm"
                                    onClick={() => { setCurrentPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                                    isDisabled={currentPage === 1}
                                    aria-label="Página anterior"
                                />

                                {Array.from({ length: totalPages }, (_, i) => i + 1)
                                    .filter(page => {
                                        // Mostrar primeira, última, e páginas próximas da atual
                                        return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 2;
                                    })
                                    .reduce((acc, page, idx, arr) => {
                                        // Adicionar "..." entre páginas não consecutivas
                                        if (idx > 0 && page - arr[idx - 1] > 1) {
                                            acc.push(<Text key={`ellipsis-${page}`} fontSize="sm" color="gray.400" px={1}>...</Text>);
                                        }
                                        acc.push(
                                            <Button
                                                key={page}
                                                size="sm"
                                                variant={currentPage === page ? "solid" : "outline"}
                                                colorScheme={currentPage === page ? "blue" : "gray"}
                                                onClick={() => { setCurrentPage(page); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                                                minW="36px"
                                            >
                                                {page}
                                            </Button>
                                        );
                                        return acc;
                                    }, [])
                                }

                                <IconButton
                                    icon={<ChevronRightIcon />}
                                    size="sm"
                                    onClick={() => { setCurrentPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                                    isDisabled={currentPage === totalPages}
                                    aria-label="Próxima página"
                                />

                                <Text fontSize="sm" color="gray.500" ml={3}>
                                    Página {currentPage} de {totalPages} ({filteredCameras.length} câmeras)
                                </Text>
                            </Flex>
                        );
                    })()}
                </>
            )}

            {/* Drawer de Filtros */}
            <Drawer isOpen={isFilterOpen} placement="right" onClose={onFilterClose} size="sm">
                <DrawerOverlay />
                <DrawerContent>
                    <DrawerCloseButton />
                    <DrawerHeader borderBottomWidth="1px">
                        <Flex align="center">
                            <FaFilter style={{ marginRight: '8px' }} />
                            Filtrar Câmeras
                        </Flex>
                    </DrawerHeader>

                    <DrawerBody>
                        <VStack spacing={4} align="stretch" py={4}>
                            <FormControl>
                                <FormLabel>Nome da Câmera</FormLabel>
                                <Input
                                    name="name"
                                    placeholder="Digite o nome da câmera"
                                    value={filters.name}
                                    onChange={handleFilterChange}
                                    leftElement={<SearchIcon color="gray.400" />}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel>Máquina</FormLabel>
                                <Select
                                    name="machineId"
                                    placeholder="Todas as máquinas"
                                    value={filters.machineId}
                                    onChange={handleFilterChange}
                                >
                                    {machines.map(machine => (
                                        <option key={machine.id} value={machine.id}>
                                            {machine.name} ({machine.ip_address})
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl>
                                <FormLabel>Zona/Localização</FormLabel>
                                <Input
                                    name="location"
                                    placeholder="Digite a zona ou localização"
                                    value={filters.location}
                                    onChange={handleFilterChange}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel>Status da Câmera</FormLabel>
                                <Select
                                    name="status"
                                    placeholder="Todos os status"
                                    value={filters.status}
                                    onChange={handleFilterChange}
                                >
                                    <option value="active">Câmeras Ativas</option>
                                    <option value="inactive">Câmeras Inativas</option>
                                </Select>
                            </FormControl>

                            <FormControl>
                                <FormLabel>Algoritmo Ativo</FormLabel>
                                <Select
                                    name="algorithmId"
                                    placeholder="Todos os algoritmos"
                                    value={filters.algorithmId}
                                    onChange={handleFilterChange}
                                >
                                    {uniqueAlgorithms.map(algorithm => (
                                        <option key={algorithm.id} value={algorithm.id}>
                                            {getAlgorithmDisplayName(algorithm)}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>

                            <Divider />

                            <VStack spacing={3}>
                                <Button
                                    colorScheme="blue"
                                    width="full"
                                    onClick={onFilterClose}
                                    isDisabled={!hasActiveFilters()}
                                >
                                    Aplicar Filtros ({getFilteredCount()})
                                </Button>

                                <Button
                                    variant="outline"
                                    width="full"
                                    onClick={resetFilters}
                                    isDisabled={!hasActiveFilters()}
                                >
                                    Limpar Filtros
                                </Button>
                            </VStack>

                            {hasActiveFilters() && (
                                <Box mt={4} p={3} bg="gray.50" borderRadius="md">
                                    <Text fontSize="sm" fontWeight="medium" mb={2}>
                                        Filtros Ativos:
                                    </Text>
                                    <VStack spacing={1} align="stretch">
                                        {filters.name && (
                                            <Text fontSize="xs" color="gray.600">
                                                • Nome: {filters.name}
                                            </Text>
                                        )}
                                        {filters.machineId && (
                                            <Text fontSize="xs" color="gray.600">
                                                • Máquina: {getMachineName(parseInt(filters.machineId))}
                                            </Text>
                                        )}
                                        {filters.location && (
                                            <Text fontSize="xs" color="gray.600">
                                                • Zona: {filters.location}
                                            </Text>
                                        )}
                                        {filters.status && (
                                            <Text fontSize="xs" color="gray.600">
                                                • Status: {filters.status === 'active' ? 'Ativas' : 'Inativas'}
                                            </Text>
                                        )}
                                        {filters.algorithmId && (
                                            <Text fontSize="xs" color="gray.600">
                                                • Algoritmo: {getAlgorithmName(parseInt(filters.algorithmId))}
                                            </Text>
                                        )}
                                    </VStack>
                                    <Text fontSize="xs" color="blue.600" mt={2} fontWeight="medium">
                                        Mostrando {getFilteredCount()} de {cameras.length} câmeras
                                    </Text>
                                </Box>
                            )}
                        </VStack>
                    </DrawerBody>
                </DrawerContent>
            </Drawer>

            <Modal isOpen={isOpen} onClose={onClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Adicionar Nova Câmera</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={4}>
                            <FormControl isRequired>
                                <FormLabel>Nome da Câmera</FormLabel>
                                <Input
                                    placeholder="Ex: Câmera Entrada Principal"
                                    name="name"
                                    value={newCamera.name}
                                    onChange={handleNewCameraInputChange}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel>Localização</FormLabel>
                                <Input
                                    placeholder="Ex: Portaria Sul"
                                    name="location"
                                    value={newCamera.location}
                                    onChange={handleNewCameraInputChange}
                                />
                            </FormControl>

                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} width="100%">
                                <FormControl>
                                    <FormLabel>Latitude</FormLabel>
                                    <Input
                                        placeholder="Ex: -23.550520"
                                        name="latitude"
                                        type="number"
                                        step="0.000001"
                                        value={newCamera.latitude}
                                        onChange={handleNewCameraInputChange}
                                    />
                                </FormControl>

                                <FormControl>
                                    <FormLabel>Longitude</FormLabel>
                                    <Input
                                        placeholder="Ex: -46.633308"
                                        name="longitude"
                                        type="number"
                                        step="0.000001"
                                        value={newCamera.longitude}
                                        onChange={handleNewCameraInputChange}
                                    />
                                </FormControl>
                            </SimpleGrid>

                            <FormControl>
                                <FormLabel>Local no mapa</FormLabel>
                                <CameraLocationPicker
                                    latitude={newCamera.latitude}
                                    longitude={newCamera.longitude}
                                    onChange={({ latitude, longitude }) => setNewCamera(prev => ({ ...prev, latitude, longitude }))}
                                />
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel>Endereço RTSP</FormLabel>
                                <Input
                                    placeholder="rtsp://user:senha@IP/streaming/channels/101"
                                    name="rtsp_address"
                                    value={newCamera.rtsp_address}
                                    onChange={handleNewCameraInputChange}
                                />
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel>Máquina</FormLabel>
                                <Select
                                    placeholder="Selecione a Máquina"
                                    name="machine_id"
                                    value={newCamera.machine_id || ''}
                                    onChange={handleNewCameraInputChange}
                                >
                                    {machines.map(machine => (
                                        <option key={machine.id} value={machine.id}>
                                            {machine.name}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* Novo: Tipos de Veículo */}
                            <FormControl>
                                <FormLabel>Tipos de Veículo (geram infração)</FormLabel>
                                <VStack align="start">
                                    {VEHICLE_TYPES.map(vt => (
                                        <Checkbox
                                            key={vt.id}
                                            isChecked={newCamera.vehicle_types?.includes(vt.id)}
                                            onChange={() => handleNewCameraToggleVehicleType(vt.id)}
                                        >
                                            {vt.label}
                                        </Checkbox>
                                    ))}
                                </VStack>
                            </FormControl>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button colorScheme="teal" onClick={handleAddCamera} mr={3}>
                            Adicionar
                        </Button>
                        <Button variant="ghost" onClick={onClose}>
                            Cancelar
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* ROI Configuration Modal */}
            <Modal
                isOpen={isRoiModalOpen}
                onClose={handleCloseRoiModal}
                size="xl"
                closeOnOverlayClick={false}
                motionPreset="none" // Desativa a animação para evitar problemas de montagem
            >
                <ModalOverlay />
                <ModalContent maxW="80vw">
                    <ModalHeader bg="green.50" borderBottomWidth="1px" borderColor="green.100">
                        <Flex alignItems="center">
                            <Text>Configurar Área de Interesse (ROI)</Text>
                            <Badge ml={3} colorScheme="green">{roiCamera?.name}</Badge>
                        </Flex>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody p={4}>
                        {roiCamera && (
                            <RoiSelector
                                key={`roi-${roiCamera.id}-${isRoiModalOpen}`} // Key dependente do estado de abertura do modal
                                cameraId={roiCamera.id}
                                onSave={handleSaveRoi}
                                onCancel={handleCloseRoiModal}
                            />
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>

            <Modal isOpen={isEditOpen} onClose={onEditClose} size="4xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader bg="blue.50" borderBottomWidth="1px" borderColor="blue.100">
                        <Flex alignItems="center">
                            <Text>Editar Câmera</Text>
                            <Badge ml={3} colorScheme="blue">{selectedCamera?.name}</Badge>
                        </Flex>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody p={6}>
                        <VStack spacing={4} align="stretch">
                            <FormControl isRequired>
                                <FormLabel>Nome da Câmera</FormLabel>
                                <Input
                                    name="name"
                                    value={selectedCamera?.name || ''}
                                    onChange={handleSelectedCameraInputChange}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel>Localização</FormLabel>
                                <Input
                                    name="location"
                                    value={selectedCamera?.location || ''}
                                    onChange={handleSelectedCameraInputChange}
                                />
                            </FormControl>

                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} width="100%">
                                <FormControl>
                                    <FormLabel>Latitude</FormLabel>
                                    <Input
                                        name="latitude"
                                        type="number"
                                        step="0.000001"
                                        value={selectedCamera?.latitude ?? ''}
                                        onChange={handleSelectedCameraInputChange}
                                    />
                                </FormControl>

                                <FormControl>
                                    <FormLabel>Longitude</FormLabel>
                                    <Input
                                        name="longitude"
                                        type="number"
                                        step="0.000001"
                                        value={selectedCamera?.longitude ?? ''}
                                        onChange={handleSelectedCameraInputChange}
                                    />
                                </FormControl>
                            </SimpleGrid>

                            <FormControl>
                                <FormLabel>Local no mapa</FormLabel>
                                <CameraLocationPicker
                                    latitude={selectedCamera?.latitude}
                                    longitude={selectedCamera?.longitude}
                                    onChange={({ latitude, longitude }) => setSelectedCamera(prev => ({ ...prev, latitude, longitude }))}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel>Trecho da rua para fluxo</FormLabel>
                                <StreetSegmentEditor
                                    camera={selectedCamera}
                                    onChange={(points) => setSelectedCamera(prev => ({ ...prev, traffic_segment: points }))}
                                />
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel>Endereço RTSP</FormLabel>
                                <Input
                                    name="rtsp_address"
                                    value={selectedCamera?.rtsp_address || ''}
                                    onChange={handleSelectedCameraInputChange}
                                />
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel>Máquina</FormLabel>
                                <Select
                                    name="machine_id"
                                    value={selectedCamera?.machine_id ?? ''}
                                    onChange={handleSelectedCameraInputChange}
                                >
                                    <option value="">— Sem máquina vinculada —</option>
                                    {machines.map(machine => (
                                        <option key={machine.id} value={machine.id}>
                                            {machine.name} ({machine.ip_address})
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>

                            <Box mt={4}>
                                <Heading size="md" mb={4}>Serviços Ativos</Heading>
                                <Divider mb={4} />

                                {uniqueAlgorithms.map(algorithm => {
                                    const serviceType = getServiceType(algorithm);
                                    const algorithmKey = getAlgorithmKey(algorithm);
                                    const activeAlgId = (selectedCamera?.algorithms || []).find(id => {
                                        const a = algorithms.find(x => x.id === id);
                                        return a && getAlgorithmKey(a) === algorithmKey;
                                    });
                                    const isActive = !!activeAlgId;
                                    const effectiveAlgorithmId = activeAlgId || algorithm.id;
                                    const serviceConfig = selectedCamera?.serviceConfigs?.[effectiveAlgorithmId];
                                    const typeIcon = getServiceTypeIcon(algorithm);

                                    return (
                                        <Flex
                                            key={algorithm.id}
                                            alignItems="center"
                                            justifyContent="space-between"
                                            p={3}
                                            bg={isActive ? "green.50" : "gray.50"}
                                            borderRadius="md"
                                            mb={2}
                                            direction="column"
                                        >
                                            <Flex width="100%" alignItems="center" justifyContent="space-between">
                                                <HStack>
                                                    <Box mr={2} fontSize="lg">
                                                        {typeIcon || getAlgorithmIcon(algorithm.id)}
                                                    </Box>
                                                    <Text fontWeight="medium">{getAlgorithmDisplayName(algorithm)}</Text>
                                                </HStack>
                                                <Switch
                                                    colorScheme="green"
                                                    isChecked={isActive}
                                                    onChange={() => handleAlgorithmToggle(getAlgorithmKey(algorithm))}
                                                />
                                            </Flex>

                                            {/* Config: Reconhecimento de Infrações (IA) */}
                                            {serviceType === 'ia_infractions' && isActive && (
                                                <Box mt={3} width="100%" bg="whiteAlpha.900" p={3} borderRadius="md" borderWidth="1px" borderColor="blue.100">
                                                    <Text fontSize="sm" color="gray.600" mb={2} fontWeight="semibold">
                                                        Tipos de infração a detectar:
                                                    </Text>
                                                    <VStack align="stretch" spacing={2}>
                                                        {INFRACTION_TYPES.map(infractionType => (
                                                            <Flex key={infractionType.id} align="center" justify="space-between" gap={3} wrap="wrap">
                                                                <Checkbox
                                                                    isChecked={(serviceConfig?.enabled_infractions || []).includes(infractionType.id)}
                                                                    onChange={() => handleToggleInfractionType(effectiveAlgorithmId, infractionType.id)}
                                                                    size="sm"
                                                                    colorScheme="blue"
                                                                >
                                                                    {infractionType.label}
                                                                </Checkbox>
                                                                <ConfidenceThresholdControl
                                                                    value={serviceConfig?.thresholds?.[infractionType.id]}
                                                                    stat={getThresholdStat(thresholdStats, effectiveAlgorithmId, 'thresholds', infractionType.id)}
                                                                    onCommit={(threshold) => handleInfractionThresholdChange(effectiveAlgorithmId, infractionType.id, threshold)}
                                                                />
                                                            </Flex>
                                                        ))}
                                                    </VStack>
                                                    <Divider my={3} />
                                                    <Text fontSize="sm" color="gray.600" mb={2} fontWeight="semibold">
                                                        Detecção de contramão:
                                                    </Text>
                                                    <Text fontSize="xs" color="gray.500" mb={2}>
                                                        Se ativado, veículos no sentido configurado gerarão infração de contramão.
                                                    </Text>
                                                    <Select
                                                        size="sm"
                                                        value={serviceConfig?.infraction_direction || ''}
                                                        onChange={(e) => handleIADirectionChange(effectiveAlgorithmId, e.target.value)}
                                                    >
                                                        {DIRECTION_OPTIONS.map(opt => (
                                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                        ))}
                                                    </Select>
                                                    <Divider my={3} />
                                                    <Text fontSize="sm" color="gray.600" mb={2} fontWeight="semibold">
                                                        Threshold das classes de contramão:
                                                    </Text>
                                                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                                                        {WRONG_WAY_THRESHOLD_TYPES.map(classType => (
                                                            <Flex key={classType.id} align="center" justify="space-between" gap={3} wrap="wrap">
                                                                <Text fontSize="sm">{classType.label}</Text>
                                                                <ConfidenceThresholdControl
                                                                    value={serviceConfig?.wrong_way_thresholds?.[classType.id]}
                                                                    stat={getThresholdStat(thresholdStats, effectiveAlgorithmId, 'wrong_way_thresholds', classType.id)}
                                                                    onCommit={(threshold) => handleWrongWayThresholdChange(effectiveAlgorithmId, classType.id, threshold)}
                                                                />
                                                            </Flex>
                                                        ))}
                                                    </SimpleGrid>
                                                </Box>
                                            )}

                                            {/* Config: Reconhecimento Facial */}
                                            {serviceType === 'face_recognition' && isActive && (
                                                <Box mt={3} width="100%" bg="whiteAlpha.900" p={3} borderRadius="md" borderWidth="1px" borderColor="purple.100">
                                                    <Text fontSize="sm" color="gray.600" mb={2} fontWeight="semibold">
                                                        Parâmetros de reconhecimento facial:
                                                    </Text>
                                                    <Text fontSize="xs" color="gray.500" mb={3}>
                                                        Ao ativar, o agent na máquina vinculada processa os frames desta câmera e compara com a galeria de rostos cadastrados.
                                                    </Text>
                                                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                                                        <Flex align="center" justify="space-between" gap={3} wrap="wrap">
                                                            <Text fontSize="sm">Similaridade mínima para match</Text>
                                                            <ConfidenceThresholdControl
                                                                value={serviceConfig?.match_threshold ?? DEFAULT_FACE_SERVICE_CONFIG.match_threshold}
                                                                onCommit={(threshold) => handleFaceThresholdChange(effectiveAlgorithmId, 'match_threshold', threshold)}
                                                            />
                                                        </Flex>
                                                        <Flex align="center" justify="space-between" gap={3} wrap="wrap">
                                                            <Text fontSize="sm">Confiança mínima da detecção</Text>
                                                            <ConfidenceThresholdControl
                                                                value={serviceConfig?.min_face_confidence ?? DEFAULT_FACE_SERVICE_CONFIG.min_face_confidence}
                                                                onCommit={(threshold) => handleFaceThresholdChange(effectiveAlgorithmId, 'min_face_confidence', threshold)}
                                                            />
                                                        </Flex>
                                                    </SimpleGrid>
                                                </Box>
                                            )}

                                            {/* Config: Faixa de Ônibus */}
                                            {serviceType === 'bus_lane' && isActive && (
                                                <Box mt={3} width="100%" bg="whiteAlpha.900" p={3} borderRadius="md" borderWidth="1px" borderColor="orange.100">
                                                    <Text fontSize="sm" color="gray.600" mb={2} fontWeight="semibold">
                                                        Veículos permitidos na faixa exclusiva:
                                                    </Text>
                                                    <Text fontSize="xs" color="gray.500" mb={2}>
                                                        Veículos <strong>marcados</strong> têm permissão de trafegar na faixa e <strong>não</strong> geram infração. Veículos desmarcados geram infração ao serem detectados.
                                                    </Text>
                                                    <VStack align="start" spacing={1} mb={3}>
                                                        {BUS_LANE_VEHICLE_TYPES.map(vt => (
                                                            <Checkbox
                                                                key={vt.id}
                                                                isChecked={(serviceConfig?.allowed_vehicles || []).includes(vt.id)}
                                                                onChange={() => handleToggleBusLaneVehicle(effectiveAlgorithmId, vt.id)}
                                                                size="sm"
                                                                colorScheme="orange"
                                                            >
                                                                {vt.label}
                                                            </Checkbox>
                                                        ))}
                                                    </VStack>
                                                    <Divider mb={3} />
                                                    <HStack justify="space-between" align="center" mb={1}>
                                                        <Text fontSize="sm" color="gray.600" fontWeight="semibold">
                                                            Ignorar Placas Cadastradas:
                                                        </Text>
                                                        <Tooltip label="Abrir tela de placas cadastradas" placement="top">
                                                            <IconButton
                                                                size="xs"
                                                                variant="ghost"
                                                                colorScheme="blue"
                                                                icon={<FaExternalLinkAlt />}
                                                                aria-label="Ir para placas cadastradas"
                                                                onClick={() => window.open('/infractions', '_blank')}
                                                            />
                                                        </Tooltip>
                                                    </HStack>
                                                    <Text fontSize="xs" color="gray.500" mb={2}>
                                                        Veículos com placa na lista de monitoradas não gerarão infração.
                                                    </Text>
                                                    <Checkbox
                                                        isChecked={serviceConfig?.ignore_whitelisted_plates || false}
                                                        onChange={() => handleToggleBusLaneIgnoreWhitelist(effectiveAlgorithmId)}
                                                        size="sm"
                                                        colorScheme="blue"
                                                        mb={3}
                                                    >
                                                        Ativo — ignorar placas cadastradas
                                                    </Checkbox>
                                                    <Divider mb={3} />
                                                    <Text fontSize="sm" color="gray.600" mb={2} fontWeight="semibold">
                                                        Sentido da via que gera infração:
                                                    </Text>
                                                    <Text fontSize="xs" color="gray.500" mb={2}>
                                                        Configure se a direção do veículo em relação à câmera gera infração por contramão.
                                                    </Text>
                                                    <Select
                                                        size="sm"
                                                        value={serviceConfig?.infraction_direction || ''}
                                                        onChange={(e) => handleBusLaneDirectionChange(effectiveAlgorithmId, e.target.value)}
                                                    >
                                                        {DIRECTION_OPTIONS.map(opt => (
                                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                        ))}
                                                    </Select>
                                                    <Divider my={3} />
                                                    <Text fontSize="sm" color="gray.600" mb={2} fontWeight="semibold">
                                                        Threshold das classes de contramão:
                                                    </Text>
                                                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                                                        {WRONG_WAY_THRESHOLD_TYPES.map(classType => (
                                                            <Flex key={classType.id} align="center" justify="space-between" gap={3} wrap="wrap">
                                                                <Text fontSize="sm">{classType.label}</Text>
                                                                <ConfidenceThresholdControl
                                                                    value={serviceConfig?.wrong_way_thresholds?.[classType.id]}
                                                                    stat={getThresholdStat(thresholdStats, effectiveAlgorithmId, 'wrong_way_thresholds', classType.id)}
                                                                    onCommit={(threshold) => handleWrongWayThresholdChange(effectiveAlgorithmId, classType.id, threshold)}
                                                                />
                                                            </Flex>
                                                        ))}
                                                    </SimpleGrid>
                                                </Box>
                                            )}

                                        </Flex>
                                    );
                                })}
                            </Box>
                        </VStack>
                    </ModalBody>
                    <ModalFooter borderTopWidth="1px" borderColor="gray.200">
                        <Button colorScheme="teal" mr={3} onClick={handleUpdateCamera}>
                            Salvar Alterações
                        </Button>
                        <Button variant="outline" onClick={onEditClose}>
                            Cancelar
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default Cameras;
